// The profile screen's data layer.
//
// ONE query for the whole screen, deliberately: identity, saved appearance and
// shifts arrive together so the screen has a single loading state instead of
// three panels resolving at different moments.
//
// Clinic membership is NOT re-fetched here — it comes from the app-wide clinic
// scope, which every screen already depends on. Asking twice for the same list
// is how two parts of one screen end up disagreeing about which clinics exist.

import React from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient.js';

export const PROFILE_QUERY = `
  query ProfilePage {
    me { id fullName medicalCode specialty bio isDentist isOnboarded }
    myAppearanceSettings { theme fontSize language }
    myPresences {
      id
      clinicId
      dayOfWeek
      specificDate
      startTimeMinutes
      endTimeMinutes
      isActive
    }
  }
`;

export const UPDATE_PROFILE = `
  mutation UpdateDoctorProfile($input: UpdateDoctorProfileInput!) {
    updateDoctorProfile(input: $input) {
      id fullName medicalCode specialty bio isDentist isOnboarded
    }
  }
`;

export const UPDATE_APPEARANCE = `
  mutation UpdateAppearanceSettings($input: AppearanceSettingsInput!) {
    updateAppearanceSettings(input: $input) { theme fontSize language }
  }
`;

export const JOIN_CLINIC = `
  mutation JoinClinicByCode($invitationCode: String!) {
    joinClinicByCode(invitationCode: $invitationCode) {
      id
      clinicId
      status
      clinic { id name city }
    }
  }
`;

export function useProfile() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    bffQuery(PROFILE_QUERY)
      .then((res) => {
        if (!alive) return;
        setData(res);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[useProfile] failed:', err?.message);
        setError(err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [nonce]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  /**
   * Presence-based partial update: ONLY the keys that actually changed go on
   * the wire, because the service treats an absent field as "leave alone" and a
   * present empty one as "blank it". Sending the whole form would erase a bio
   * the dentist never touched.
   */
  const saveProfile = React.useCallback(
    async (changes) => {
      const result = await bffMutation(UPDATE_PROFILE, { input: changes });
      setData((prev) => (prev ? { ...prev, me: result?.updateDoctorProfile || prev.me } : prev));
      return result?.updateDoctorProfile;
    },
    [],
  );

  const saveAppearance = React.useCallback(async (patch) => {
    const result = await bffMutation(UPDATE_APPEARANCE, { input: patch });
    setData((prev) =>
      prev ? { ...prev, myAppearanceSettings: result?.updateAppearanceSettings || prev.myAppearanceSettings } : prev,
    );
    return result?.updateAppearanceSettings;
  }, []);

  return {
    me: data?.me || null,
    appearance: data?.myAppearanceSettings || null,
    presences: data?.myPresences || [],
    loading,
    error,
    reload,
    saveProfile,
    saveAppearance,
  };
}

export default useProfile;
